import { useGetWorkspaceMeApiV1Suspense } from "@/api/__generated__/api.schema";
import { WorkspaceMemberRole } from "@/api/__generated__/models";
import { useWorkspaceId } from "@/hooks/auth/use-workspace";

export const WorkspaceMemberRoleOrder = {
  [WorkspaceMemberRole.OWNER]: 1,
  [WorkspaceMemberRole.ADMIN]: 2,
  [WorkspaceMemberRole.MEMBER]: 3,
} as const;

export function useWorkspaceMemberRole() {
  const workspaceId = useWorkspaceId();
  const {
    data: {
      data: {
        item: { role: memberRole },
      },
    },
  } = useGetWorkspaceMeApiV1Suspense(workspaceId);

  return { data: { memberRole, isOverAdmin: WorkspaceMemberRoleOrder[memberRole] <= WorkspaceMemberRoleOrder.ADMIN } };
}

interface AllowedProps {
  role: WorkspaceMemberRole;
  exact?: boolean;
  children: React.ReactNode;
  fallback?: React.ReactNode;
}

export function Allowed({ role: requiredRole, exact, children, fallback }: AllowedProps) {
  const {
    data: { memberRole },
  } = useWorkspaceMemberRole();

  if (exact === true && requiredRole === memberRole) {
    return children;
  }

  if (exact !== true && WorkspaceMemberRoleOrder[requiredRole] >= WorkspaceMemberRoleOrder[memberRole]) {
    return children;
  }

  return fallback;
}

type AllowedChildProps = Omit<AllowedProps, "role" | "exact">;

function OwnerOnly({ children, fallback }: AllowedChildProps) {
  return (
    <Allowed role={WorkspaceMemberRole.OWNER} exact={true} fallback={fallback}>
      {children}
    </Allowed>
  );
}

function Admin({ children, fallback }: AllowedChildProps) {
  return (
    <Allowed role={WorkspaceMemberRole.ADMIN} exact={false} fallback={fallback}>
      {children}
    </Allowed>
  );
}

Allowed.OwnerOnly = OwnerOnly;
Allowed.Admin = Admin;
